import * as Styled from "./EditEventPage.styles";

import { APIError, PageContainer } from "../../components";
import LoadingModal from "../../modals/LoadingModal/LoadingModal";
import { Box, CircularProgress, Stack, useTheme } from "@mui/material";
import {
  DetailsSection,
  EventFeedbackSection,
  PrimaryContactSection,
  SignupSurveySection,
} from "../CreateEventPage/sections";
import {
  EDIT_EVENT,
  GET_EVENT_IMAGE_BASE_64,
  GET_USERS_EMAILS,
  TRIGGER_EVENT_EMAIL_ACTION,
} from "../../services/endpoints";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Buffer } from "buffer";
import { Event } from "../../types/event";
import { Room } from "../../types/room";
import { PreviewEmailModal, RoomFinderModal } from "../../modals";
import { RootState } from "../../store/store";
import { UserState } from "../../types/redux";
import axiosAPI from "../../services/axios";
import { combineDayJSToUnix } from "../../utils/utils";
import { fileTypeFromBuffer } from "file-type";
import formOptions from "../CreateEventPage/formOptions";
import { gatherDefault64 } from "../../modals/PreviewEmailModal/images";
import { getAllEvents } from "../../store/actions/eventsActions";
import { getAllUserEvents } from "../../store/actions/userEventsActions";
import { getEvent } from "../../store/actions/editEventActions";
import imageCompression from "browser-image-compression";
import { prepareEditEventData } from "../../utils/event_utils";
import { useAppDispatch } from "../../store/hooks";
import { useSelector } from "react-redux";
import useUpdateChangesCount from "../../hooks/useUpdateChangesCount";

type EditEventForm = {
  signUpCreator: boolean;
  title: string;
  description: string;
  eventDate?: Dayjs | null;
  startDateTime?: Dayjs | null;
  endDateTime?: Dayjs | null;
  dressCode: string;
  admissionCost: number;
  eventImageFile: File | null;
  format: string;
  office: string;
  eventTags: string[];
  eventType: string;
  rooms: Room[];
  location: string;
  transportationParkingDetails: string;
  primaryContacts: string[];
  includeTeamsLink: string;
  teamsLink?: string;
  feedbackLink: string;
  includeSignUpSurvey: string;
  signUpSurvey: { type: string; question: string }[];
  recipientList: string;
  bypassSendEmail: boolean;
  googleMapsLocationLink?: string;
};

const EditEventPage = () => {
  const { id } = useParams();

  const environment = process.env.REACT_APP_ENVIRONMENT || "local";

  useEffect(() => {
    if (id && id !== "") {
      dispatch(getEvent(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const event: Event = useSelector((state: any) => state.edit.data);
  const eventLoading: boolean = useSelector((state: any) => state.edit.loading);
  const eventError: boolean = useSelector((state: any) => state.edit.error);

  const currentTime = dayjs();
  const roundToNearest30Minutes = (dateTime: Dayjs) => {
    const minutes = dateTime.minute();
    const roundedMinutes = Math.round(minutes / 30) * 30;
    return dateTime.startOf("hour").add(roundedMinutes, "minute");
  };

  const [initialValues, setInitialValues] = useState({});

  const [placeholderFileName, setPlaceholderFileName] = useState("");

  const [showPreviewEmailModal, setShowPreviewEmailModal] = useState(false);

  const userInfo = useSelector((state: any) => {
    const userState = state.user as UserState;
    return userState;
  });

  const formMethods = useForm({
    defaultValues: {
      title: "",
      description: "",
      eventDate: dayjs(),
      startDateTime: roundToNearest30Minutes(currentTime),
      endDateTime: roundToNearest30Minutes(currentTime).add(30, "minute"),
      dressCode: "",
      admissionCost: 0,
      format: "",
      eventImageFile: null,
      eventTags: [],
      office: "",
      signUpCreator: true,
      location: "",
      transportationParkingDetails: "",
      primaryContacts: [],
      includeTeamsLink: "no",
      teamsLink: "",
      feedbackLink: "",
      includeSignUpSurvey: "yes",
      signUpSurvey: [],
      rooms: [],
      eventType: "",
      recipientList: [],
      bypassSendEmail: false,
      googleMapsLocationLink: "",
    },
    mode: "onChange",
  });

  const setFormValues = (event: Event) => {
    const setFormValue = (name: keyof EditEventForm, value: any) => {
      formMethods.setValue(name, value);
      setInitialValues((prevValues) => ({ ...prevValues, [name]: value }));
    };

    setFormValue("title", event.title);
    setFormValue("description", event.description);
    setFormValue("eventDate", dayjs.unix(event.startDateTime));
    setFormValue("startDateTime", dayjs.unix(event.startDateTime));
    setFormValue("endDateTime", dayjs.unix(event.endDateTime));
    setFormValue("dressCode", event.dressCode);
    setFormValue("admissionCost", event.admissionCost);
    setFormValue("format", event.format);
    setFormValue("location", event.location);
    setFormValue("includeTeamsLink", event.teamsLink !== "" ? "yes" : "no");
    setFormValue("teamsLink", event.teamsLink);
    setFormValue("eventImageFile", null);
    setFormValue("office", event.office);
    setFormValue("eventTags", event.eventTags as never[]);
    setFormValue(
      "transportationParkingDetails",
      event.transportationParkingDetails
    );
    setFormValue("feedbackLink", "");
    setFormValue(
      "primaryContacts",
      event.primaryContacts.map((contact) => {
        const user = users.find((user) => user.name === contact);
        return {
          name: contact,
          email: user?.email || "",
        };
      })
    );
    setFormValue(
      "includeSignUpSurvey",
      event.signUpSurvey.length > 0 ? "yes" : "no"
    );
    setFormValue("signUpSurvey", event.signUpSurvey);
    setFormValue("rooms", event.rooms as never[]);
    setPlaceholderFileName(
      event.eventHeroS3Url ? `${event.eventId}-hero-image` : ""
    );
    setFormValue("eventType", event.eventType);
    setFormValue("googleMapsLocationLink", event.googleMapsLocationLink || "");
    setFormValue("recipientList", event.recipientList);
    setFormValue("bypassSendEmail", event.recipientList.length === 0);
  };

  useEffect(() => {
    if (event) {
      const isSuperAdmin = userInfo.role === "ADMIN:SUPER";
      const isEventOwner =
        userInfo.name === event.eventOrganizerName &&
        userInfo.email === event.eventOrganizerEmail;
      const canEditEvent = isEventOwner || isSuperAdmin;

      if (
        !loading &&
        (dayjs.unix(event.endDateTime).isBefore(dayjs()) ||
          !canEditEvent ||
          event.status !== "ACTIVE") &&
        id === event.eventId
      ) {
        navigate("/events", {
          state: {
            alert: {
              severity: "error",
              message:
                "The event you tried to access cannot be edited because it is either already complete, a draft event, was cancelled, or you do not own it.",
            },
          },
        });
      }

      setFormValues(event);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const [loading, setLoading] = useState(false);
  const [createEventError, setCreateEventError] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { errors } = formMethods.formState;
  const hasErrors = Object.keys(errors).length > 0;

  const { loading: roomsLoading } = useSelector(
    (state: RootState) => state.room
  );

  const includeSignUpSurvey = formMethods.watch("includeSignUpSurvey");
  const format = formMethods.watch("format");

  const [showRoomFinderModal, setShowRoomFinderModal] = useState(false);
  const rooms = formMethods.watch("rooms");
  const eventType = formMethods.watch("eventType");
  const [roomFinderError, setRoomFinderError] = useState(false);
  const [roomFinderErrorMessage, setRoomFinderErrorMessage] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [users, setUsers] = useState<
    {
      name: string;
      email: string;
    }[]
  >([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await axiosAPI.request({
          url: GET_USERS_EMAILS(),
          method: "GET",
        });

        const transformedUsers = data.result.users.map(
          (user: { email: string; firstName: string; lastName: string }) => ({
            email: user.email,
            name: `${user.firstName} ${user.lastName}`.trim(),
          })
        );

        setUsers(transformedUsers);
      } catch (error) {
        console.error("Error fetching user emails:", error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (eventType === "Off-site") {
      formMethods.setValue("rooms", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType]);

  useEffect(() => {
    if (format === "Virtual") {
      formMethods.setValue("rooms", []);
      formMethods.setValue("location", "");
      formMethods.setValue("eventType", "Off-site");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format]);

  const toISOString = (timestamp: number) =>
    new Date(timestamp * 1000).toISOString();

  const bypassSendEmail = formMethods.watch("bypassSendEmail");

  const sendEventEmail = async (
    event: Event,
    rooms: Room[],
    eventImage: string
  ) => {
    if (!eventImage && event.eventHero) {
      try {
        const { data } = await axiosAPI.get(
          GET_EVENT_IMAGE_BASE_64(event.eventId, event.eventHero)
        );
        const buffer = Buffer.from(data.result, "base64");
        const uint8Array = new Uint8Array(buffer);
        const fileType = await fileTypeFromBuffer(uint8Array);
        const mimeType = fileType ? fileType.mime : "image/jpeg";
        eventImage = `data:${mimeType};base64,${data.result}`;
      } catch (error) {
        console.error("Failed to get event image base64:", error);
      }
    }
    const payload = await editEventEmailPayload(event, rooms, eventImage);
    await axiosAPI.post(TRIGGER_EVENT_EMAIL_ACTION(event.eventId), payload);
  };

  const editEventEmailPayload = async (
    {
      title,
      description,
      startDateTime,
      endDateTime,
      format,
      eventOrganizerName,
      eventOrganizerEmail,
      location,
      recipientList,
    }: Event,
    rooms: Room[],
    eventImage: string
  ) => {
    return {
      action: "update",
      eventTitle: title,
      eventDescription: description,
      eventStartDateTime: toISOString(startDateTime),
      eventEndDateTime: toISOString(endDateTime),
      roomLocation: rooms.map((room: Room) => room.email).join(";"),
      eventInvitees: recipientList.join(";"),
      eventOrganizer: eventOrganizerName,
      eventOrganizerEmail: eventOrganizerEmail,
      eventFormat: format,
      eventImage: eventImage === "" ? gatherDefault64 : eventImage,
      eventLocation: location,
      roomLocationOld: event.rooms.map((room: Room) => room.email).join(";"),
      eventFormatOld: event.format,
    };
  };

  const onSubmit: SubmitHandler<any> = async (data) => {
    setLoading(true);
    setModalOpen(true);
    setLoadingMessage("Saving your changes...");

    try {
      const { startDateTime, endDateTime } = getDateTimes(data);
      const newEventHero = await handleImageCompression(data);
      const editEventData = prepareEditEventData(
        data,
        startDateTime,
        endDateTime,
        newEventHero,
        event,
        eventType,
        placeholderFileName,
        userInfo
      );

      const response = await axiosAPI.put(
        EDIT_EVENT(event.eventId),
        filterUndefinedValues(editEventData)
      );
      const { result }: { result: Event } = response.data;
      if (!bypassSendEmail) {
        setLoadingMessage("Sending out updated invitations...");
        const finalizingTimer = setTimeout(() => {
          setLoadingMessage("Finalizing your details...");
        }, 10000);
        try {
          await sendEventEmail(result, data.rooms, newEventHero || "");
        } finally {
          clearTimeout(finalizingTimer);
        }
      }

      dispatch(getAllEvents());
      dispatch(getAllUserEvents(userInfo.email));
      navigate("/manage");
    } catch (error: any) {
      handleError(error);
      setCreateEventError(true);
    } finally {
      setLoading(false);
      setModalOpen(false);
    }
  };

  const getDateTimes = (data: any) => {
    const startDateTime = combineDayJSToUnix(
      data.eventDate as Dayjs,
      data.startDateTime as Dayjs
    );
    const endDateTime = combineDayJSToUnix(
      data.eventDate as Dayjs,
      data.endDateTime as Dayjs
    );
    return { startDateTime, endDateTime };
  };

  const handleImageCompression = async (data: any) => {
    if (data.eventImageFile && placeholderFileName === "FILE_CHANGED") {
      const options = {
        maxSizeMB: 3.0,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      try {
        const compressedFile = await imageCompression(
          data.eventImageFile as File,
          options
        );
        return await imageCompression.getDataUrlFromFile(compressedFile);
      } catch (error) {
        console.error("Image compression failed:", error);
      }
    }
    return undefined;
  };

  const filterUndefinedValues = (data: any) => {
    return Object.fromEntries(
      Object.entries(data).filter(([_, v]) => v !== undefined)
    );
  };

  const handleError = (error: any) => {
    const errorMessage = error.response?.data?.data;

    if (errorMessage.includes("Room is not available for the specified time")) {
      setRoomFinderError(true);
      setRoomFinderErrorMessage(
        "The room that you have selected is no longer available. Please try again."
      );
    } else {
      setCreateEventError(true);
      console.error("Failed to update event:", error);
    }
  };

  const changesCount =
    useUpdateChangesCount(formMethods, initialValues, event, eventLoading) +
    (placeholderFileName === "FILE_CHANGED" ? 1 : 0);

  const resetForm = () => {
    setFormValues(event);
    document.documentElement.scrollTop = 0;
  };

  if (eventLoading) {
    return (
      <PageContainer>
        <Styled.PageLoaderContainer>
          <CircularProgress />
        </Styled.PageLoaderContainer>
      </PageContainer>
    );
  }

  if (eventError && !eventLoading) {
    return <APIError />;
  }

  return (
    <PageContainer>
      {event && (
        <RoomFinderModal
          name={"rooms"}
          control={formMethods.control}
          setValue={formMethods.setValue}
          open={showRoomFinderModal}
          onClose={() => {
            setShowRoomFinderModal(false);
          }}
          createdEvent={event}
          roomFinderError={roomFinderError}
          roomFinderErrorMessage={roomFinderErrorMessage}
          setRoomFinderError={setRoomFinderError}
          setRoomFinderErrorMessage={setRoomFinderErrorMessage}
        />
      )}
      {createEventError ? (
        <APIError
          onClick={() => {
            setCreateEventError(false);
          }}
        />
      ) : (
        <Styled.PaddedContainer>
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit)}>
              <PreviewEmailModal
                open={showPreviewEmailModal}
                onClose={() => {
                  setShowPreviewEmailModal(false);
                }}
                formData={formMethods.getValues()}
              />
              <Styled.HeaderStack
                direction={{ mobile: "column", desktop: "row" }}
                width="100%"
              >
                <Styled.HeaderText variant="h1" gutterBottom>
                  Edit Event
                </Styled.HeaderText>
              </Styled.HeaderStack>
              <DetailsSection
                formMethods={formMethods}
                formOptions={formOptions}
                environment={environment}
                event={event}
                format={format}
                roomFinderError={roomFinderError}
                roomFinderErrorMessage={roomFinderErrorMessage}
                rooms={rooms}
                roomsLoading={roomsLoading}
                setShowRoomFinderModal={setShowRoomFinderModal}
                placeholderFileName={placeholderFileName}
                setPlaceholderFileName={setPlaceholderFileName}
                eventType={eventType}
                userInfo={userInfo}
                eventDescription={event?.description}
                bypassSendEmail={bypassSendEmail}
              />
              <PrimaryContactSection formMethods={formMethods} users={users} />
              <SignupSurveySection
                formMethods={formMethods}
                includeSignUpSurvey={includeSignUpSurvey}
              />
              <EventFeedbackSection />
              {modalOpen ? (
                <LoadingModal
                  open={modalOpen}
                  onClose={() => setModalOpen(false)}
                  message={loadingMessage}
                />
              ) : (
                <Box>
                  <Box
                    display="flex"
                    justifyContent={{ mobile: "center", desktop: "end" }}
                  >
                    {hasErrors && (
                      <Styled.ErrorMessage>
                        Please correct all errors before publishing the event
                      </Styled.ErrorMessage>
                    )}
                  </Box>
                  <Styled.FormActionStack
                    direction={{ mobile: "column", desktop: "row" }}
                    spacing={theme.spacing(1)}
                    width="100%"
                    alignItems="flex-end"
                  >
                    <Styled.ActionTextButton
                      onClick={() => {
                        setShowPreviewEmailModal(true);
                      }}
                    >
                      Preview Event Email
                    </Styled.ActionTextButton>
                    <Stack
                      direction={{ mobile: "column", desktop: "row-reverse" }}
                      spacing={theme.spacing(1)}
                      width="100%"
                      justifyContent="flex-start"
                    >
                      <Styled.ActionButton
                        variant="contained"
                        type="submit"
                        disabled={
                          ((roomsLoading || roomFinderError) &&
                            eventType === "On-site") ||
                          changesCount === 0 ||
                          hasErrors
                        }
                      >
                        {(roomsLoading || roomFinderError) &&
                        eventType === "On-site"
                          ? roomsLoading
                            ? "Loading rooms..."
                            : "Fix Error In Room Finder"
                          : `Save Changes (${changesCount})`}
                      </Styled.ActionButton>
                      <Styled.ActionButton
                        variant="outlined"
                        onClick={resetForm}
                        disabled={
                          ((roomsLoading || roomFinderError) &&
                            eventType === "On-site") ||
                          changesCount === 0 ||
                          hasErrors
                        }
                      >
                        Undo All Changes
                      </Styled.ActionButton>
                    </Stack>
                  </Styled.FormActionStack>
                </Box>
              )}
            </form>
          </FormProvider>
        </Styled.PaddedContainer>
      )}
    </PageContainer>
  );
};

export default EditEventPage;
